import { DatePicker } from '@heroui/react';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import {
  parseAbsolute,
  parseDate,
  today,
  getLocalTimeZone,
  DateValue,
  toCalendarDate,
  toCalendarDateTime,
} from '@internationalized/date';
import { useTZDayjs } from '@/context/TimezoneContext';
import { debounce } from 'lodash';

type DisabledDateRange = [DateValue, DateValue];

/** Matches @react-stately/datepicker: minute granularity requires `minute` on the value object. */
function coerceForPickerGranularity(
  dv: DateValue | null,
  withTime: boolean
): DateValue | null {
  if (!dv) return null;
  if (!withTime) {
    try {
      return toCalendarDate(dv);
    } catch {
      return null;
    }
  }
  if ('minute' in dv) return dv;
  try {
    return toCalendarDateTime(dv);
  } catch {
    return null;
  }
}

type DateSelectorNextUIProps = {
  control?: any;
  controlName?: string;
  withTime?: boolean;
  label?: string;
  hideLabel?: boolean;
  ariaLabel?: string;
  value?: string;
  onChange?: (date: string) => void;
  errorMessage?: string;
  minValue?: DateValue;
  maxValue?: DateValue;
  disabledRanges?: DisabledDateRange[];
  isDateUnavailable?: (date: DateValue) => boolean;
  futureDatesOnly?: boolean;
  size?: 'sm' | 'md' | 'lg';
  isInvalid?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
};

export default function DateSelectorNextUI({
  control,
  controlName,
  withTime,
  value,
  onChange,
  label,
  hideLabel,
  ariaLabel,
  errorMessage,
  minValue,
  maxValue,
  disabledRanges,
  isDateUnavailable,
  futureDatesOnly,
  size = 'md',
  isInvalid,
  isDisabled,
  isClearable = true,
}: DateSelectorNextUIProps) {
  if (!control && onChange)
    return (
      <DateSelector
        key={withTime ? 'with-time' : 'date-only'}
        value={value}
        onChange={onChange}
        withTime={withTime}
        label={label}
        hideLabel={hideLabel}
        ariaLabel={ariaLabel}
        errorMessage={errorMessage}
        minValue={minValue}
        maxValue={maxValue}
        disabledRanges={disabledRanges}
        isDateUnavailable={isDateUnavailable}
        futureDatesOnly={futureDatesOnly}
        size={size}
        isInvalid={isInvalid}
        isDisabled={isDisabled}
        isClearable={isClearable}
      />
    );
  else if (control && controlName)
    return (
      <Controller
        control={control}
        name={controlName}
        render={({ field, fieldState }) => (
          <DateSelector
            key={withTime ? 'with-time' : 'date-only'}
            value={field.value}
            onChange={field.onChange}
            withTime={withTime}
            label={label}
            hideLabel={hideLabel}
            ariaLabel={ariaLabel}
            errorMessage={fieldState.error?.message}
            minValue={minValue}
            maxValue={maxValue}
            disabledRanges={disabledRanges}
            isDateUnavailable={isDateUnavailable}
            futureDatesOnly={futureDatesOnly}
            size={size}
            isInvalid={isInvalid}
            isDisabled={isDisabled}
            isClearable={isClearable}
          />
        )}
      />
    );
}

function DateSelector({
  value,
  onChange,
  withTime,
  label,
  hideLabel,
  ariaLabel,
  errorMessage,
  minValue,
  maxValue,
  disabledRanges = [],
  isDateUnavailable: customIsDateUnavailable,
  futureDatesOnly,
  size = 'md',
  isInvalid,
  isDisabled,
  isClearable = true,
}: {
  value?: string;
  onChange: (date: string) => void;
  withTime?: boolean;
  label?: string;
  hideLabel?: boolean;
  ariaLabel?: string;
  errorMessage?: string;
  minValue?: DateValue;
  maxValue?: DateValue;
  disabledRanges?: DisabledDateRange[];
  isDateUnavailable?: (date: DateValue) => boolean;
  futureDatesOnly?: boolean;
  size?: 'sm' | 'md' | 'lg';
  isDisabled?: boolean;
  isInvalid?: boolean;
  isClearable?: boolean;
}) {
  const tzDayjs = useTZDayjs();
  const inferTimezone = useCallback(
    (input?: string) => {
      const fromInput = input ? (tzDayjs(input) as any)?.$x?.$timezone : null;
      if (typeof fromInput === 'string' && fromInput.length > 0) return fromInput;

      const fromNow = (tzDayjs() as any)?.$x?.$timezone;
      if (typeof fromNow === 'string' && fromNow.length > 0) return fromNow;

      return getLocalTimeZone();
    },
    [tzDayjs]
  );

  const activeTimezone = useMemo(() => inferTimezone(), [inferTimezone]);

  // Convert prop value to DateValue for local state
  const propValueAsDateValue = useMemo(() => {
    if (!value) return null;
    try {
      return !withTime
        ? parseDate(tzDayjs(value).format('YYYY-MM-DD'))
        : parseAbsolute(tzDayjs(value).toISOString(), inferTimezone(value));
    } catch {
      return null;
    }
  }, [value, withTime, tzDayjs, inferTimezone]);

  // Local state for smooth typing experience
  const [localDateValue, setLocalDateValue] = useState<DateValue | null>(propValueAsDateValue);

  const valueForPicker = useMemo(
    () => coerceForPickerGranularity(localDateValue, !!withTime),
    [localDateValue, withTime]
  );

  // Track if we're updating from prop (to avoid feedback loop)
  const isUpdatingFromProp = useRef(false);

  // Sync local state when prop value changes externally
  useEffect(() => {
    if (!isUpdatingFromProp.current) {
      setLocalDateValue(propValueAsDateValue);
    }
    isUpdatingFromProp.current = false;
  }, [propValueAsDateValue]);

  // Debounced function to sync to parent
  const debouncedOnChange = useMemo(
    () =>
      debounce((date: DateValue | null) => {
        if (!date) {
          onChange('');
          return;
        }

        const dayjsDate = withTime
          ? tzDayjs(date.toDate(activeTimezone))
          : tzDayjs(date.toString());

        if (dayjsDate.isValid()) {
          isUpdatingFromProp.current = true;
          onChange(dayjsDate.toISOString());
        }
      }, 500),
    [onChange, withTime, tzDayjs, activeTimezone]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedOnChange.cancel();
    };
  }, [debouncedOnChange]);

  // Combine all date restriction logic
  const isDateUnavailable = React.useCallback(
    (date: DateValue) => {
      // If custom function is provided, use it
      if (customIsDateUnavailable && customIsDateUnavailable(date)) {
        return true;
      }

      // Check if date is in any disabled range
      const isInDisabledRange = disabledRanges.some(
        ([start, end]) => date.compare(start) >= 0 && date.compare(end) <= 0
      );

      if (isInDisabledRange) {
        return true;
      }

      // If futureDatesOnly is true, disable past dates and today
      if (futureDatesOnly) {
        const now = today(getLocalTimeZone());
        if (date.compare(now) <= 0) {
          return true;
        }
      }

      return false;
    },
    [customIsDateUnavailable, disabledRanges, futureDatesOnly]
  );

  const handleDateChange = useCallback(
    (date: DateValue | null) => {
      // Update local state immediately for smooth typing
      setLocalDateValue(date);
      // Debounce the sync to parent
      debouncedOnChange(date);
    },
    [debouncedOnChange]
  );

  const handleClear = useCallback(() => {
    setLocalDateValue(null);
    debouncedOnChange.cancel(); // Cancel any pending debounced calls
    onChange('');
  }, [onChange, debouncedOnChange]);

  return (
    <div>
      <DatePicker
        key={`datepicker-${withTime ? 'with-time' : 'date-only'}`}
        label={hideLabel ? undefined : label}
        aria-label={hideLabel ? (ariaLabel ?? label ?? 'Date') : undefined}
        showMonthAndYearPickers
        granularity={withTime ? 'minute' : 'day'}
        hideTimeZone
        variant='bordered'
        value={valueForPicker}
        className='py-1'
        classNames={{
          inputWrapper: `bg-white !border shadow-none`,
          label: 'font-light uppercase text-[10px]',
        }}
        onChange={handleDateChange}
        errorMessage={errorMessage}
        isInvalid={!!errorMessage || isInvalid}
        isDateUnavailable={isDateUnavailable}
        minValue={
          minValue || (futureDatesOnly ? today(getLocalTimeZone()).add({ days: 1 }) : undefined)
        }
        maxValue={maxValue}
        size={size}
        isDisabled={isDisabled}
      />
      {valueForPicker && isClearable && (
        <div className='flex justify-end'>
          <button onClick={handleClear} className='link-primary link text-xs font-light'>
            Clear
          </button>
        </div>
      )}
    </div>
  );
}
